import { API } from 'src/utils/interceptor'
import { ORDER_ONLY } from 'src/utils/roles'
import { ollieFoodSafetyPath } from 'src/utils/ollieActionDeepLinks'
import {
  buildInvoicesPath,
  buildReceiptingPath,
} from 'src/utils/reconciliationHealthApi'

/** Fired after a receipt, reconcile, check, or approval so Home reloads. */
export const VENUE_HOME_CHANGED_EVENT = 'oms:venue-home-changed'

export function notifyVenueHomeChanged() {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(VENUE_HOME_CHANGED_EVENT))
}

export function venueHomeOutstandingCount(home) {
  const count = Number(home?.summary?.outstanding)
  if (!Number.isFinite(count) || count <= 0) return 0
  return Math.min(Math.floor(count), 100000)
}

export function formatVenueHomeBadgeLabel(count) {
  const n = venueHomeOutstandingCount({ summary: { outstanding: count } })
  if (n <= 0) return null
  if (n > 99) return '99+'
  return String(n)
}

export function formatVenueHomeBadgeA11yLabel(count) {
  const n = venueHomeOutstandingCount({ summary: { outstanding: count } })
  if (n <= 0) return null
  if (n === 1) return '1 thing still to do'
  const shown = n > 99 ? '99+' : String(n)
  return `${shown} things still to do`
}

const TASK_CATEGORY_LABELS = {
  checklist: 'Custom tasks',
  recipeReview: 'Review recipes',
  stockCount: 'Stock count',
  underPar: 'Under par',
  unconfirmedOrder: 'Orders',
  unmatchedInvoice: 'Invoices',
  grossProfit: 'Gross Profit',
  goodsReceipting: 'Goods receipting',
  finance: 'Finance',
  foodSafety: 'Food Safety',
  approvals: 'Approvals',
}

const TASK_STATUS_LABELS = {
  overdue: 'Overdue',
  dueToday: 'Due today',
  outstanding: 'Outstanding',
}

const TASK_ACTIONS = {
  receipting: 'Open delivery',
  invoice: 'Open invoice',
  foodSafety: 'Open check',
  menuBuilder: 'Review recipes',
  stock: 'Count stock',
  shoppingList: "Today's order",
  orders: 'Open order',
  grossProfit: 'Add sales',
  approvals: 'Review approval',
}

export function withVenueHomeReturn(path) {
  if (!path) return path
  const [pathname, query = ''] = String(path).split('?')
  const params = new URLSearchParams(query)
  params.set('from', 'home')
  return `${pathname}?${params.toString()}`
}

/** Home path when this screen was opened from a Home task, otherwise null. */
export function venueHomeReturnPath(venueId, search) {
  if (!venueId) return null
  const from = new URLSearchParams(String(search || '').replace(/^\?/, '')).get(
    'from',
  )
  if (from !== 'home') return null
  return `/v/${venueId}/home`
}

/** The task is still on the latest Home list. */
export function venueHomeTaskStillOpen(home, taskId) {
  const id = String(taskId || '')
  if (!id || !home) return false
  return (home.tasks || []).some(task => String(task?.id || '') === id)
}

export function venueHomeTaskAction(task, venueId) {
  const screen = task?.navigation?.screen
  const label = task?.actionLabel || TASK_ACTIONS[screen] || 'Do this today'
  if (!venueId || !screen) return { label, to: null }
  const base = `/v/${venueId}`
  const finish = to => ({ label, to: to ? withVenueHomeReturn(to) : null })
  if (screen === 'receipting') {
    const purchaseOrderNumber = String(
      task.navigation.purchaseOrderNumber || '',
    ).trim()
    return finish(
      purchaseOrderNumber
        ? buildReceiptingPath(venueId, 'all', { q: purchaseOrderNumber })
        : `${base}/purchasing/receipting`,
    )
  }
  if (screen === 'invoice') {
    const invoiceId = task.navigation.invoiceId
    return finish(
      invoiceId
        ? buildInvoicesPath(venueId, { invoiceId })
        : `${base}/purchasing/invoices`,
    )
  }
  if (screen === 'approvals') {
    const token = String(task.navigation.token || '').trim()
    return finish(
      token
        ? `${base}/approvals?token=${encodeURIComponent(token)}`
        : `${base}/approvals`,
    )
  }
  if (screen === 'menuBuilder') {
    return finish(`${base}/menu-builder?gpChanged=1`)
  }
  if (screen === 'stock') return finish(`${base}/stockTakes`)
  if (screen === 'shoppingList') return finish(`${base}/products`)
  if (screen === 'orders') return finish(`${base}/purchasing/orders`)
  if (screen === 'grossProfit') return finish(`${base}/purchasing/gross-profit`)
  if (screen === 'foodSafety') {
    const nav = task.navigation || {}
    const path = ollieFoodSafetyPath(venueId, {
      section: nav.section,
      check: nav.definitionId,
    })
    const extra = new URLSearchParams()
    const localDate = String(nav.localDate || '').trim()
    const dueTime = String(nav.dueTime || '').trim()
    if (localDate) extra.set('date', localDate)
    if (dueTime) extra.set('dueTime', dueTime)
    const extraQuery = extra.toString()
    return finish(
      extraQuery
        ? `${path}${path.includes('?') ? '&' : '?'}${extraQuery}`
        : path,
    )
  }
  return { label, to: null }
}

/** First task the Home card should name, skipping the check today’s job already shows. */
export function venueHomeTasksLeadTitle(tasks = [], jobText = '') {
  const job = String(jobText || '')
    .trim()
    .toLowerCase()
  const next = tasks.find(task => {
    const title = String(task?.title || '').trim()
    if (!title) return false
    if (!job || title.length < 4) return true
    return !job.includes(title.toLowerCase())
  })
  return next?.title || ''
}

/** What a task card shows before anyone opens the record. */
export function venueHomeTaskCard(task, venueId) {
  const category = TASK_CATEGORY_LABELS[task?.category] || 'Task'
  return {
    id: String(task?.id || ''),
    title: String(task?.title || 'Task').trim() || 'Task',
    detail: String(task?.detail || '').trim(),
    category,
    status:
      task?.statusLabel || TASK_STATUS_LABELS[task?.status] || 'Outstanding',
    due:
      task?.status === 'dueToday' || task?.status === 'overdue'
        ? task?.statusLabel || TASK_STATUS_LABELS[task.status]
        : '',
    action: venueHomeTaskAction(task, venueId),
  }
}

export const VENUE_HOME_STATUS_FILTERS = [
  { id: 'all', label: 'All outstanding' },
  { id: 'dueToday', label: 'Due today' },
  { id: 'overdue', label: 'Overdue' },
]

/** Due today and overdue come only from the status the source already set. */
export function venueHomeTasksForStatus(tasks = [], statusFilter = 'all') {
  if (statusFilter === 'dueToday') {
    return tasks.filter(task => task?.status === 'dueToday')
  }
  if (statusFilter === 'overdue') {
    return tasks.filter(task => task?.status === 'overdue')
  }
  return tasks
}

/** Hero counts from the Home summary. Empty categories are already left out. */
export function venueHomeHeroCounts(summary) {
  const number = value => {
    const count = Number(value)
    return Number.isFinite(count) && count > 0 ? Math.floor(count) : 0
  }
  const categories = (
    Array.isArray(summary?.byCategory) ? summary.byCategory : []
  )
    .map(item => ({
      category: String(item?.category || ''),
      label: String(item?.label || '').trim(),
      count: number(item?.count),
    }))
    .filter(item => item.category && item.label && item.count > 0)
  return {
    outstanding: number(summary?.outstanding),
    dueToday: number(summary?.dueToday),
    overdue: number(summary?.overdue),
    categories,
  }
}

const CATEGORY_CAPABILITY = {
  goodsReceipting: 'invoices',
  finance: 'invoices',
  foodSafety: 'foodSafetyComplete',
  recipeReview: 'menuBuilder',
  stockCount: 'stockCounts',
  underPar: 'ordering',
  unconfirmedOrder: 'ordering',
  unmatchedInvoice: 'invoices',
  grossProfit: 'reports',
  approvals: 'orderApprovals',
}

const CATEGORY_ORDER = [
  'checklist',
  'goodsReceipting',
  'finance',
  'foodSafety',
  'recipeReview',
  'stockCount',
  'underPar',
  'unconfirmedOrder',
  'unmatchedInvoice',
  'grossProfit',
  'approvals',
]

/** Checklist is personal. Other categories need the capability to act on them. */
const ORDER_ONLY_HIDDEN_PERMISSIONS = new Set([
  'invoices',
  'creditNotes',
  'orderApprovals',
  'menuBuilder',
])

export function venueHomeCategoryAllowed(category, canAct, task, role) {
  if (category === 'checklist') return true
  const capability = task?.requiredPermission || CATEGORY_CAPABILITY[category]
  if (role === ORDER_ONLY && ORDER_ONLY_HIDDEN_PERMISSIONS.has(capability))
    return false
  if (!capability || typeof canAct !== 'function') return false
  return Boolean(canAct(capability))
}

export function venueHomeActableTasks(tasks = [], canAct, role) {
  return tasks.filter(task =>
    venueHomeCategoryAllowed(task?.category, canAct, task, role),
  )
}

export function venueHomeCategoryFilters(tasks = []) {
  const counts = new Map()
  for (const task of tasks) {
    const category = task?.category
    if (!category) continue
    counts.set(category, (counts.get(category) || 0) + 1)
  }
  return CATEGORY_ORDER.filter(category => counts.has(category)).map(
    category => ({
      category,
      label: TASK_CATEGORY_LABELS[category] || category,
      count: counts.get(category),
    }),
  )
}

export function venueHomeCountsFromTasks(tasks = []) {
  let dueToday = 0
  let overdue = 0
  const counts = new Map()
  for (const task of tasks) {
    if (task?.status === 'dueToday') dueToday += 1
    else if (task?.status === 'overdue') overdue += 1
    const category = task?.category
    if (!category) continue
    const current = counts.get(category) || {
      category,
      label: TASK_CATEGORY_LABELS[category] || category,
      count: 0,
    }
    current.count += 1
    counts.set(category, current)
  }
  return {
    outstanding: tasks.length,
    dueToday,
    overdue,
    categories: CATEGORY_ORDER.filter(category => counts.has(category)).map(
      category => counts.get(category),
    ),
  }
}

/** Area failures from Home. A warning means the day is not clear. */
export function venueHomeWarnings(home) {
  return (Array.isArray(home?.warnings) ? home.warnings : [])
    .map(warning => String(warning?.message || '').trim())
    .filter(Boolean)
}

/** A clear day is an empty list and no area failure. Zeros are not outstanding work. */
export function venueHomeIsClearDay({ tasks = [], warnings = [] } = {}) {
  const warningLines = Array.isArray(warnings)
    ? warnings.filter(warning =>
        String(warning?.message || warning || '').trim(),
      )
    : []
  return (
    (Array.isArray(tasks) ? tasks : []).length === 0 &&
    warningLines.length === 0
  )
}

/** Keep a payload only when it was requested for, and belongs to, this venue. */
export function venueHomeForRoute({ venueId, requestedVenueId, home } = {}) {
  if (!venueId || String(requestedVenueId || '') !== String(venueId))
    return null
  if (!home || String(home.venueId || '') !== String(venueId)) return null
  return home
}

const SHARED_VENUE_HOME_CATEGORIES = new Set([
  'goodsReceipting',
  'finance',
  'foodSafety',
  'approvals',
  'recipeReview',
  'stockCount',
  'underPar',
  'unconfirmedOrder',
  'unmatchedInvoice',
  'grossProfit',
])

/**
 * The list after Refresh. Shared tasks come only from this fetch, so a
 * colleague finishing the work removes it. Checklist rows are this
 * account's own list from the same fetch.
 */
export function venueHomeTasksAfterRefresh(
  // The list already on screen. Refresh replaces it from the server.
  // eslint-disable-next-line no-unused-vars
  previous = [],
  next = [],
) {
  const incoming = Array.isArray(next) ? next : []
  return incoming.filter(task => {
    const category = String(task?.category || '')
    if (category === 'checklist') return true
    return SHARED_VENUE_HOME_CATEGORIES.has(category)
  })
}

const venueHomeWidgetPath = (home, venueId, widgetId, path) => {
  if (!venueId) return null
  const widgets = Array.isArray(home?.widgets) ? home.widgets : []
  const included = widgets.some(widget => widget?.id === widgetId)
  return included ? path : null
}

/** Shopping lists, when this Home includes the Place an order widget. */
export function venueHomePlaceOrderPath(home, venueId) {
  return venueHomeWidgetPath(
    home,
    venueId,
    'placeOrder',
    `/v/${venueId}/products`,
  )
}

/** Stock, when this Home already includes the Stock widget. */
export function venueHomeStockPath(home, venueId) {
  return venueHomeWidgetPath(home, venueId, 'stock', `/v/${venueId}/stockTakes`)
}

/** Menu Builder, when this Home already includes that widget. */
export function venueHomeMenuBuilderPath(home, venueId) {
  return venueHomeWidgetPath(
    home,
    venueId,
    'menuBuilder',
    `/v/${venueId}/menu-builder`,
  )
}

/** Food Safety, when this Home already includes that widget. */
export function venueHomeFoodSafetyPath(home, venueId) {
  return venueHomeWidgetPath(
    home,
    venueId,
    'foodSafety',
    `/v/${venueId}/food-safety`,
  )
}

const VENUE_HOME_SHORTCUTS = {
  placeOrder: {
    label: 'Place order',
    subtitle: 'Review your shopping list and send to suppliers.',
    cta: 'Place order',
    testId: 'venue-home-place-order',
    primary: true,
    path: venueId => `/v/${venueId}/products`,
  },
  receipting: {
    label: 'Receipt deliveries',
    subtitle:
      'Check deliveries in so your figures match the bill, and raise credit with suppliers.',
    cta: 'Receipt deliveries',
    testId: 'venue-home-receipting',
    path: venueId => `/v/${venueId}/purchasing/receipting`,
  },
  stock: {
    label: 'Stock Take',
    subtitle: "Count what's on the shelf. We'll tell you what to order.",
    cta: 'Make a count',
    testId: 'venue-home-stock',
    path: venueId => `/v/${venueId}/stockTakes`,
  },
  menuBuilder: {
    label: 'Menu Builder',
    subtitle: 'Recipes that stay current as your prices move',
    cta: 'Open Menu Builder',
    testId: 'venue-home-menu-builder',
    path: venueId => `/v/${venueId}/menu-builder`,
  },
  foodSafety: {
    label: 'Food Safety',
    subtitle:
      'Today’s kitchen checks — temps, cooks, cools, deliveries, and cleaning.',
    cta: 'Open Food Safety',
    testId: 'venue-home-food-safety',
    path: venueId => `/v/${venueId}/food-safety`,
  },
}

/** Shortcuts in the saved widget order. Tasks and stats are not buttons. */
export function venueHomeShortcuts(home, venueId) {
  if (!venueId) return []
  return (home?.widgets || [])
    .map(widget => {
      const shortcut = VENUE_HOME_SHORTCUTS[widget?.id]
      if (!shortcut) return null
      return {
        id: widget.id,
        label: shortcut.label,
        subtitle: shortcut.subtitle,
        cta: shortcut.cta,
        secondaryCta: shortcut.secondaryCta || '',
        testId: shortcut.testId,
        primary: Boolean(shortcut.primary),
        to: shortcut.path(venueId),
      }
    })
    .filter(Boolean)
}

export const VENUE_HOME_ASK_OLLIE_LABEL = 'Ask Ollie'

const VENUE_HOME_OLLIE_PROMPTS = [
  { id: 'today', text: 'What needs my attention today?' },
  {
    id: 'deliveries',
    text: 'Show my outstanding deliveries',
    capability: 'invoices',
  },
  {
    id: 'foodSafety',
    text: 'What Food Safety tasks are due today?',
    capability: 'foodSafetyComplete',
  },
  { id: 'order', text: 'Build my next order', capability: 'ordering' },
]

/**
 * Ask Ollie only when the panel can open and a question can be sent.
 * Home's task list does not wait on that.
 */
export function venueHomeShowAskOllie({
  available = false,
  atLimit = false,
} = {}) {
  return Boolean(available) && !atLimit
}

/** Ready-made questions for Home. Today's list is always first; the rest follow what this role can open. */
export function venueHomeOlliePrompts(can = () => false, role) {
  const prompts = VENUE_HOME_OLLIE_PROMPTS.filter(prompt => {
    if (!prompt.capability) return true
    if (role === ORDER_ONLY && prompt.capability === 'invoices') return false
    return can(prompt.capability)
  })
  if (role !== ORDER_ONLY) return prompts.slice(0, 3)
  const today = prompts.filter(prompt => prompt.id === 'today')
  const order = prompts.filter(prompt => prompt.id === 'order')
  const rest = prompts.filter(
    prompt => prompt.id !== 'today' && prompt.id !== 'order',
  )
  return [...today, ...order, ...rest].slice(0, 3)
}

export function venueHomeWidgetDraft(widgets = []) {
  return (widgets || []).map(widget => widget?.id).filter(Boolean)
}

export function toggleVenueHomeWidget(ids = [], id, shown) {
  const list = (ids || []).filter(item => item && item !== id)
  return shown ? [...list, id] : list
}

export function moveVenueHomeWidget(ids = [], id, direction) {
  const list = [...(ids || [])]
  const index = list.indexOf(id)
  const next = index + direction
  if (index < 0 || next < 0 || next >= list.length) return list
  const [item] = list.splice(index, 1)
  list.splice(next, 0, item)
  return list
}

export async function saveVenueHomeWidgets({
  venueId,
  widgetIds,
  apiPut = API.put,
} = {}) {
  if (!venueId) throw new Error('Choose a venue first.')
  const body = await apiPut('api2', `venue/${venueId}/home/widgets`, {
    body: { widgetIds },
  })
  if (!body?.success)
    throw new Error(body?.message || 'Could not save widgets.')
  return body.data?.widgets || []
}

export async function restoreVenueHomeWidgets({
  venueId,
  apiDelete = API.delete,
} = {}) {
  if (!venueId) throw new Error('Choose a venue first.')
  const body = await apiDelete('api2', `venue/${venueId}/home/widgets`)
  if (!body?.success) {
    throw new Error(body?.message || 'Could not restore the default widgets.')
  }
  return body.data?.widgets || []
}

/** Gross Profit, when this Home already includes the stats widget. */
export function venueHomeStatsPath(home, venueId) {
  return venueHomeWidgetPath(
    home,
    venueId,
    'stats',
    `/v/${venueId}/purchasing/gross-profit`,
  )
}

/** This month’s Gross Profit glance: sales, OMS spend ex VAT, and GP%. */
export function venueHomeStatsGlance(data) {
  if (!data || typeof data !== 'object') return null
  const sales = Number(data.totalSaleAmount)
  const days = Array.isArray(data.salesPerDay) ? data.salesPerDay : []
  const omsSpend = days.reduce(
    (sum, day) => sum + (Number(day?.purchasesOnOMS) || 0),
    0,
  )
  const gp = data.gpp == null || data.gpp === '' ? null : String(data.gpp)
  return {
    sales: Number.isFinite(sales) ? sales : 0,
    omsSpend,
    gp,
  }
}

export async function fetchVenueHomeStats({
  venueId,
  from,
  to,
  apiGet = API.get,
} = {}) {
  if (!venueId || !from || !to) return null
  const data = await apiGet(
    'api',
    `gross-profit/sales-per-day?fromDate=${from}&toDate=${to}&venue=${venueId}`,
  )
  return venueHomeStatsGlance(data)
}

export async function fetchVenueHome({ venueId, apiGet = API.get } = {}) {
  if (!venueId) return null
  const body = await apiGet('api2', `venue/${venueId}/home`, {
    headers: { 'Cache-Control': 'no-cache' },
  })
  if (!body?.success) {
    throw new Error(body?.message || 'Failed to load Home.')
  }
  return body.data || null
}

export function venueHomeChecklistItemId(task) {
  const source = String(task?.sourceRecordId || '').trim()
  if (source) return source
  const id = String(task?.id || '')
  return id.startsWith('checklist:') ? id.slice('checklist:'.length) : ''
}

/** Note after the due line Express already builds. */
export function venueHomeChecklistNote(task) {
  const detail = String(task?.detail || '').trim()
  const marker = ' · '
  const at = detail.indexOf(marker)
  if (at === -1) return /^Due\b/i.test(detail) ? '' : detail
  return detail.slice(at + marker.length)
}

const checklistFailure = (error, fallback) => {
  const message = error?.response?.data?.message || error?.message || fallback
  return new Error(message)
}

export async function tickVenueHomeChecklist({
  venueId,
  itemId,
  apiPost = API.post,
} = {}) {
  if (!venueId || !itemId)
    throw new Error('That checklist item could not be found.')
  try {
    const body = await apiPost(
      'api2',
      `venue/${venueId}/home/checklist/${itemId}/tick`,
      { body: {} },
    )
    if (!body?.success) {
      throw new Error(body?.message || 'Could not tick that checklist item.')
    }
    return body.data || null
  } catch (error) {
    throw checklistFailure(error, 'Could not tick that checklist item.')
  }
}

export async function updateVenueHomeChecklist({
  venueId,
  itemId,
  title,
  note = '',
  cadence = 'daily',
  weekday,
  monthDay,
  apiPatch = API.patch,
} = {}) {
  if (!venueId || !itemId)
    throw new Error('That checklist item could not be found.')
  try {
    const body = await apiPatch(
      'api2',
      `venue/${venueId}/home/checklist/${itemId}`,
      {
        body: {
          title,
          note,
          cadence,
          ...(cadence === 'weekly' ? { weekday } : {}),
          ...(cadence === 'monthly' ? { monthDay } : {}),
        },
      },
    )
    if (!body?.success) {
      throw new Error(body?.message || 'Could not update that checklist item.')
    }
    return body.data || null
  } catch (error) {
    throw checklistFailure(error, 'Could not update that checklist item.')
  }
}

export async function shareVenueHomeChecklist({
  venueId,
  itemId,
  venueIds,
  apiPost = API.post,
} = {}) {
  if (!venueId || !itemId)
    throw new Error('That checklist item could not be found.')
  try {
    const body = await apiPost(
      'api2',
      `venue/${venueId}/home/checklist/${itemId}/share`,
      {
        body: { venueIds },
      },
    )
    if (!body?.success) {
      throw new Error(body?.message || 'Could not share that checklist item.')
    }
    return body.data || null
  } catch (error) {
    throw checklistFailure(error, 'Could not share that checklist item.')
  }
}

export async function deleteVenueHomeChecklist({
  venueId,
  itemId,
  apiDelete = API.delete,
} = {}) {
  if (!venueId || !itemId)
    throw new Error('That checklist item could not be found.')
  try {
    const body = await apiDelete(
      'api2',
      `venue/${venueId}/home/checklist/${itemId}`,
    )
    if (!body?.success) {
      throw new Error(body?.message || 'Could not delete that checklist item.')
    }
    return body.data || null
  } catch (error) {
    throw checklistFailure(error, 'Could not delete that checklist item.')
  }
}

export const VENUE_HOME_CHECKLIST_WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
]

export async function fetchVenueHomeChecklist({
  venueId,
  apiGet = API.get,
} = {}) {
  if (!venueId) return []
  const body = await apiGet('api2', `venue/${venueId}/home/checklist`, {
    headers: { 'Cache-Control': 'no-cache' },
  })
  if (!body?.success) {
    throw new Error(body?.message || 'Could not load your custom tasks.')
  }
  return Array.isArray(body.data?.items) ? body.data.items : []
}

export async function createVenueHomeChecklist({
  venueId,
  title,
  note = '',
  cadence = 'daily',
  weekday,
  monthDay,
  apiPost = API.post,
} = {}) {
  if (!venueId) throw new Error('Choose a venue first.')
  const body = await apiPost('api2', `venue/${venueId}/home/checklist`, {
    body: {
      title,
      note,
      cadence,
      ...(cadence === 'weekly' ? { weekday } : {}),
      ...(cadence === 'monthly' ? { monthDay } : {}),
    },
  })
  if (!body?.success) {
    throw new Error(body?.message || 'Could not add that checklist item.')
  }
  return body.data || null
}

export async function fetchFoodSafetySectionLayout({
  venueId,
  apiGet = API.get,
} = {}) {
  if (!venueId) throw new Error('Choose a venue first.')
  const body = await apiGet(
    'api2',
    `venue/${venueId}/home/food-safety-sections`,
    {
      headers: { 'Cache-Control': 'no-cache' },
    },
  )
  if (!body?.success)
    throw new Error(body?.message || 'Could not load the Food Safety layout.')
  return {
    sectionIds: body.data?.sectionIds ?? null,
    hiddenIds: body.data?.hiddenIds || [],
  }
}

export async function saveFoodSafetySectionLayout({
  venueId,
  sectionIds,
  hiddenIds,
  apiPut = API.put,
} = {}) {
  if (!venueId) throw new Error('Choose a venue first.')
  const body = await apiPut(
    'api2',
    `venue/${venueId}/home/food-safety-sections`,
    {
      body: { sectionIds, hiddenIds },
    },
  )
  if (!body?.success)
    throw new Error(body?.message || 'Could not save the Food Safety layout.')
  return body.data
}

export async function restoreFoodSafetySectionLayout({
  venueId,
  apiDelete = API.delete,
} = {}) {
  if (!venueId) throw new Error('Choose a venue first.')
  const body = await apiDelete(
    'api2',
    `venue/${venueId}/home/food-safety-sections`,
  )
  if (!body?.success)
    throw new Error(body?.message || 'Could not restore Food Safety.')
  return body.data
}
